import { useMemo, useState, useEffect } from "react";
import { can } from "@/lib/rbac";
import { ProductionRow } from "@/components/ProductionRow";
import { BatchRow } from "@/components/batch/BatchRow";
import { consolidateOrders } from "@/lib/batches";
import { useT } from "@/lib/i18n";

// Status ordering per view. Dashboard surfaces at-risk (Delayed) higher than the Schedule view.
const STATUS_ORDER = {
  schedule: { "In Progress": 0, Pending: 1, Delayed: 2, Completed: 3 },
  dashboard: { "In Progress": 0, Delayed: 1, Pending: 2, Completed: 3 },
};

const HEADERS = [
  { key: "col.orderId" },
  { key: "col.product" },
  { key: "col.qty", align: "text-right" },
  { key: "col.progress" },
  { key: "col.due" },
  { key: "col.priority" },
  { key: "col.line" },
  { key: "col.stage" },
  { key: "common.status" },
  { key: "common.actions", align: "text-right" },
];

export default function ProductionTable({ orders, machines = [], role, onEdit, onDelete, onSuggestFix, onLogStage, onShipStage, onReceiveStage, onHistory, filterDelayed, sortByStatus = null, highlightCode = null, onHighlightDone, title, subtitle, headerActions, emptyText = "No orders to display.", hideEdit = false, onOpenOrder, spotlightOverdue = false, isAdmin = false, currentUsername, onApplyBomRefresh, onAcknowledgeBom, groupBatches = false, onBatchLog = null }) {
  const editable = can.manageOrders(role);
  const t = useT();
  const machinesById = useMemo(() => new Map(machines.map((m) => [m.id, m])), [machines]);
  // Accordion: only one order's stage-gate cards are open at a time.
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));
  // Jump-to-order: expand + flash the targeted order when navigated to from a work order.
  useEffect(() => {
    if (!highlightCode) return;
    const match = orders.find((o) => o.order_code === highlightCode);
    if (match) setExpandedId(match.id);
    const timer = setTimeout(() => onHighlightDone && onHighlightDone(), 3200);
    return () => clearTimeout(timer);
  }, [highlightCode, orders, onHighlightDone]);
  let rows = filterDelayed ? orders.filter((o) => o.status === "Delayed") : orders;
  if (sortByStatus) {
    const order = STATUS_ORDER[sortByStatus] || STATUS_ORDER.schedule;
    rows = [...rows].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  }
  // Batch view: fold 2+ active orders sharing a product+recipe into one BatchRow (Title Case sublabels).
  const displayRows = groupBatches ? consolidateOrders(rows) : rows;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="production-health-table">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">{title || t("prod.health")}</h3>
          <p className="text-xs text-slate-400">
            {subtitle || `${rows.length} order${rows.length !== 1 ? "s" : ""} ${filterDelayed ? "· filtered: delayed" : "· all statuses"} · Stage-Gate view — expand an order to log stage quantities`}
          </p>
        </div>
        {headerActions}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              {HEADERS.map((h) => (
                <th key={h.key} className={`px-4 py-3 font-semibold ${h.align || ""}`}>{t(h.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 && (
              <tr>
                <td colSpan={HEADERS.length} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {emptyText}
                </td>
              </tr>
            )}
            {displayRows.map((o) => (
              o.members ? (
                <BatchRow
                  key={o.id}
                  batch={o}
                  editable={editable}
                  canManage={editable}
                  expanded={expandedId === o.id}
                  onToggle={toggle}
                  onEdit={onEdit}
                  onHistory={onHistory}
                  onBatchLog={onBatchLog}
                  onLogStage={onLogStage}
                  onShipStage={onShipStage}
                  onReceiveStage={onReceiveStage}
                />
              ) : (
              <ProductionRow
                key={o.id}
                order={o}
                line={machinesById.get(o.line_id)}
                editable={editable}
                canManage={editable}
                expanded={expandedId === o.id}
                onToggle={toggle}
                highlight={o.order_code === highlightCode}
                hideEdit={hideEdit}
                onOpenOrder={onOpenOrder}
                spotlightOverdue={spotlightOverdue}
                isAdmin={isAdmin}
                currentUsername={currentUsername}
                onApplyBomRefresh={onApplyBomRefresh}
                onAcknowledgeBom={onAcknowledgeBom}
                onEdit={onEdit}
                onDelete={onDelete}
                onSuggestFix={onSuggestFix}
                onLogStage={onLogStage}
                onShipStage={onShipStage}
                onReceiveStage={onReceiveStage}
                onHistory={onHistory}
              />
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
